import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  TextField,
  Autocomplete,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { SelectAll } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomLabel from '../CustomLabel';
import ErrorFeedbackP from '../ErrorFeedbackP';
import AutocompleteRenderGroup from '../AutocompleteRenderGroup';

export const customAutocompleteStyles = {
  '& .MuiInput-underline:after': {
    borderBottom: 'none',
    transitionProperty: 'none',
  },
  '& .MuiInputBase-root': {
    backgroundColor: '#edebeb !important',
    minHeight: '38px',
  },
  '& .MuiInputBase-fullWidth': {
    borderRadius: '4px !important',
  },
  '& .MuiAutocomplete-popper': {
    width: '100%',
  },
  '& .MuiFormControl-marginNormal': {
    marginTop: 0,
  },
  '& .MuiChip-deleteIcon': {
    color: '#ffffff !important',
  },
  '& .MuiAutocomplete-inputRoot[class*="MuiInput-root"]': {
    position: 'relative',
    paddingBottom: '1px',
    '@media (max-width:850px)': {
      maxWidth: '100%',
    },
    '&:hover .selectAllButton, &:hover .MuiAutocomplete-clearIndicator, &:hover .MuiAutocomplete-popupIndicator':
      {
        opacity: 1,
      },
  },
  '& .MuiInputBase-inputAdornedStart': {
    paddingLeft: '0px !important',
  },
  '& .selectAllButton, .MuiAutocomplete-clearIndicator, .MuiAutocomplete-popupIndicator':
    {
      opacity: 0,
      transition: 'opacity 200ms ease-in-out',
      zIndex: 1,
    },
};

const CustomAutocomplete = ({
  tags,
  selected,
  onSelectTags,
  onBlur,
  id,
  label,
  required,
  touched,
  error,
  loading,
  disabled,
  deletableTag,
  noMarginTop,
  labelInside,
  variant,
  multiple,
  readOnly,
  info,
  limitTags,
  disableCloseOnSelect,
  placeholder,
  hideSelectAll,
  grouped,
  getGroupLabel,
  colorMap,
  ...props
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = React.useState('');

  const handleSelectAll = () => {
    onSelectTags?.(tags, 'selectAll');
  };

  const handleInputBlur = event => {
    setInputValue('');
    onBlur(event);
  };

  const groupedProps = grouped
    ? {
        isOptionEqualToValue: (option, value) => option.label === value,
        getOptionLabel: option => option.label,
        getOptionValue: option => option.value,
        groupBy: option => option.type,
        renderGroup: params => (
          <AutocompleteRenderGroup
            key={params.key}
            groupTitle={getGroupLabel(params.group)}
          >
            {params.children}
          </AutocompleteRenderGroup>
        ),
      }
    : {};

  return (
    <>
      {!labelInside && (
        <CustomLabel
          noMarginTop={noMarginTop}
          required={required}
          htmlFor={id}
          info={info}
        >
          {label}
        </CustomLabel>
      )}
      <Box
        sx={{
          ...customAutocompleteStyles,
          '& .MuiInput-root': {
            position: 'relative',
            paddingLeft: selected.length > 0 ? 0 : '8px',
          },
        }}
      >
        <Autocomplete
          id={id}
          data-testid={id}
          multiple={multiple}
          loading={loading}
          options={tags}
          disabled={disabled}
          readOnly={readOnly}
          disableCloseOnSelect={disableCloseOnSelect}
          getOptionDisabled={opt => !deletableTag(opt)}
          value={selected || []}
          onChange={(_event, value, reason) => onSelectTags(value, reason)}
          noOptionsText={t('components.autocomplete.empty_list')}
          includeInputInList
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (disableCloseOnSelect && reason === 'reset') return;
            if (reason === 'clear') {
              setInputValue('');
              return;
            }
            setInputValue(newInputValue);
          }}
          sx={{
            // MUI puts a padding right of 56px when two icons are shown, so make it 84px to include the select-all icon
            '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiAutocomplete-inputRoot':
              {
                paddingRight: hideSelectAll ? '56px' : '84px',
              },
            // MUI puts a padding right of 30px when one icon is shown, so make it 56px to include the select-all icon
            '&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot': {
              paddingRight: hideSelectAll ? '30px' : '56px',
            },
          }}
          renderInput={({ InputProps, ...rest }) => (
            <TextField
              {...rest}
              variant={variant}
              data-testid="autocomplete-input"
              onBlur={handleInputBlur}
              fullWidth
              placeholder={selected.length === 0 ? placeholder : ''}
              InputProps={{
                ...InputProps,
                endAdornment: hideSelectAll ? (
                  InputProps.endAdornment
                ) : (
                  <InputAdornment
                    position="end"
                    sx={{
                      // override autocomplete endAdornment styles and replicate it to InputAdornment wrapper to include the custom icon
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      '.MuiAutocomplete-endAdornment': {
                        position: 'relative',
                        top: 0,
                        right: 0,
                      },
                    }}
                  >
                    <IconButton
                      size="small"
                      className="selectAllButton"
                      onClick={handleSelectAll}
                      disabled={selected.length === tags.length}
                      title={t('components.autocomplete.select_all')}
                    >
                      <SelectAll fontSize="small" />
                    </IconButton>
                    {InputProps.endAdornment}
                  </InputAdornment>
                ),
              }}
            />
          )}
          renderTags={(value, getTagProps) => {
            const visible = limitTags < 0 ? value : value.slice(0, limitTags);
            return (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  maxWidth: '100%',
                }}
              >
                {visible.map((option, idx) => {
                  const tagProps = getTagProps({ index: idx });
                  if (!deletableTag(option)) {
                    delete tagProps.onDelete;
                  }
                  return (
                    <Chip
                      key={option}
                      label={option}
                      {...tagProps}
                      sx={{
                        maxWidth: '100%',
                        height: 'auto', // chip can grow vertically
                        bgcolor: colorMap
                          ? colorMap[option.toLowerCase()]
                          : '#02BDBD',
                        color: '#FFF',
                        display: 'flex', // ensure flexbox
                        alignItems: 'center', // ✅ vertically center children
                        '& .MuiChip-label': {
                          display: 'block',
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflow: 'visible',
                          textOverflow: 'clip',
                          py: 0.25,
                        },
                        '& .MuiChip-deleteIcon': {
                          alignSelf: 'center', // ✅ force icon to center
                          mt: 0, // reset any top margin
                        },
                      }}
                    />
                  );
                })}
                {limitTags >= 0 && value.length > limitTags && (
                  <Chip
                    label={`+${value.length - limitTags}`}
                    sx={{ height: '1.8rem', bgcolor: '#AAA', color: '#FFF' }}
                  />
                )}
              </Box>
            );
          }}
          {...groupedProps}
          {...props}
        />
      </Box>
      {touched && error && <ErrorFeedbackP touched={touched} error={error} />}
    </>
  );
};

CustomAutocomplete.defaultProps = {
  touched: null,
  error: null,
  loading: false,
  disabled: false,
  required: false,
  deletableTag: () => true,
  getGroupLabel: label => label,
  noMarginTop: false,
  labelInside: false,
  multiple: true,
  readOnly: false,
  variant: 'standard',
  info: null,
  limitTags: -1,
  onBlur: () => null,
  disableCloseOnSelect: true,
  hideSelectAll: false,
  placeholder: '',
};

const groupedTagsPropTypes = PropTypes.shape({
  value: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
});

CustomAutocomplete.propTypes = {
  tags: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      groupedTagsPropTypes,
      PropTypes.object,
    ]),
  ).isRequired,
  selected: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectTags: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  deletableTag: PropTypes.func,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  noMarginTop: PropTypes.bool,
  labelInside: PropTypes.bool,
  multiple: PropTypes.bool,
  readOnly: PropTypes.bool,
  touched: PropTypes.any,
  error: PropTypes.string,
  variant: PropTypes.string,
  info: PropTypes.string,
  placeholder: PropTypes.string,
  limitTags: PropTypes.number,
  disableCloseOnSelect: PropTypes.bool,
  hideSelectAll: PropTypes.bool,
  grouped: PropTypes.bool,
  getGroupLabel: PropTypes.func,
  colorMap: PropTypes.object,
};

export default CustomAutocomplete;
